/**
 * Voyage Setup
 *
 * Shown before a voyage is under way. Collects the crew and provisions the ship
 * sails with, then resolves Stormwrack's course-setting check and hands the
 * result to the navigation state machine — a failed check means the ship sets
 * out already off track and nobody aboard knows it.
 */

import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../@/components/ui/card';
import { Button } from '../@/components/ui/button';
import { Input } from '../@/components/ui/input';
import { Label } from '../@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../@/components/ui/select';
import { useSession } from '../session-context';
import { VESSEL_DATA } from '../types/vessel';
import {
  VoyageDistance, DestinationFamiliarity, StartPointKnowledge, ChartQuality,
  CourseSettingParams, calculateCourseSettingDC, formatCourseSettingParams,
  processCourseSettingCheck
} from '../utils/course-setting';
import { chartCourse } from '../utils/nav-utils';
import { defaultNavigationState } from '../types/navigation-types';

const DISTANCES: VoyageDistance[] = ['close', 'distant'];
const FAMILIARITIES: DestinationFamiliarity[] = [
  'very familiar', 'studied carefully', 'seen once', 'uncertain', 'mythical or legendary'
];
const START_KNOWLEDGE: StartPointKnowledge[] = [
  'well known', 'uncertain', 'only guessed at', 'completely unknown'
];
const CHART_QUALITIES: ChartQuality[] = ['none', 'excellent', 'poor', 'extremely poor', 'false'];

export const VoyageSetup: React.FC = () => {
  const {
    gameState, isDM, updateGameState, startVoyage, updateNavigation, addNavLogEntries
  } = useSession();

  const userIsDM = isDM();
  const vessel = VESSEL_DATA[gameState?.selectedVessel ?? 'Caravel'];

  const [destination, setDestination] = useState('');
  const [distance, setDistance] = useState<VoyageDistance>('distant');
  const [familiarity, setFamiliarity] = useState<DestinationFamiliarity>('studied carefully');
  const [startKnowledge, setStartKnowledge] = useState<StartPointKnowledge>('well known');
  const [chartQuality, setChartQuality] = useState<ChartQuality>('none');

  const [navigatorName, setNavigatorName] = useState(gameState?.navigatorName ?? 'The navigator');
  const [crewSize, setCrewSize] = useState<number>(vessel?.complement ?? 0);
  const [estimatedDays, setEstimatedDays] = useState<number>(14);
  const [provisionsOverridden, setProvisionsOverridden] = useState(false);
  const [provisions, setProvisions] = useState<number>((vessel?.complement ?? 0) * 14);

  const [roll, setRoll] = useState<string>('');
  const [skillBonus, setSkillBonus] = useState<string>('0');
  const [isStarting, setIsStarting] = useState(false);

  // Re-seed the crew from the vessel whenever the DM picks a different ship.
  useEffect(() => {
    setCrewSize(vessel?.complement ?? 0);
  }, [vessel]);

  // Provisions follow crew × days until the DM types their own number.
  useEffect(() => {
    if (!provisionsOverridden) setProvisions(crewSize * estimatedDays);
  }, [crewSize, estimatedDays, provisionsOverridden]);

  const params: CourseSettingParams = {
    distance, familiarity, startPointKnowledge: startKnowledge, chartQuality
  };
  const dc = calculateCourseSettingDC(params);

  if (!userIsDM) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No Voyage Under Way</CardTitle>
          <CardDescription>
            The DM is plotting the course. The voyage will appear here once it begins.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const handleBeginVoyage = async () => {
    const d20 = parseInt(roll, 10);
    if (!destination.trim() || isNaN(d20)) return;

    setIsStarting(true);
    try {
      const result = processCourseSettingCheck(params, d20, parseInt(skillBonus, 10) || 0);

      await updateGameState('voyageDestination', {
        name: destination.trim(),
        distance,
        familiarity,
        startPointKnowledge: startKnowledge,
        chartQuality,
        courseSettingDC: result.dc,
        courseSettingResult: {
          roll: result.roll,
          totalBonus: result.totalBonus,
          total: result.total,
          success: result.success,
          missedBy: result.missedBy,
          missDistance: result.missDistance
        }
      });

      await updateGameState('crewSize', crewSize);
      await updateGameState('provisions', provisions);
      await updateGameState('navigatorName', navigatorName.trim() || 'The navigator');
      await updateGameState('voyageDay', 0);
      await updateGameState('consecutiveFailures', { navigation: 0 });

      // Seed the navigation state machine. Day 0 is the moment of departure.
      const charted = chartCourse(gameState?.navigation ?? defaultNavigationState, {
        success: result.success,
        period: 0,
        pilotName: navigatorName.trim() || 'The navigator'
      });
      await updateNavigation(charted.nav);
      await addNavLogEntries(charted.logs);

      await startVoyage();
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Ship's Company</CardTitle>
          <CardDescription>
            Who sails, and what they eat. Each crew member consumes one provision
            point per day at sea.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="crew-size">Crew size</Label>
            <Input
              id="crew-size"
              type="number"
              min={0}
              value={crewSize}
              onChange={(e) => setCrewSize(Math.max(0, parseInt(e.target.value, 10) || 0))}
            />
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {vessel
                ? `${vessel.name}: complement ${vessel.complement}, watch ${vessel.watch}${
                    vessel.rowers ? ` plus ${vessel.rowers} rowers` : ''
                  }.`
                : 'Select a vessel on the Ship Combat tab to seed this from its complement.'}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="estimated-days">Estimated voyage length (days)</Label>
            <Input
              id="estimated-days"
              type="number"
              min={1}
              value={estimatedDays}
              onChange={(e) => setEstimatedDays(Math.max(1, parseInt(e.target.value, 10) || 1))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="provisions">Provisions</Label>
            <Input
              id="provisions"
              type="number"
              min={0}
              value={provisions}
              onChange={(e) => {
                setProvisionsOverridden(true);
                setProvisions(Math.max(0, parseInt(e.target.value, 10) || 0));
              }}
            />
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {provisionsOverridden
                ? `Feeds ${crewSize} crew for ${crewSize > 0 ? Math.floor(provisions / crewSize) : '∞'} days.`
                : `Defaults to crew × days = ${crewSize} × ${estimatedDays}.`}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="navigator">Navigator</Label>
            <Input
              id="navigator"
              value={navigatorName}
              onChange={(e) => setNavigatorName(e.target.value)}
              placeholder="Who makes the daily navigation check"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Set a Course</CardTitle>
          <CardDescription>
            Stormwrack course setting. Fail this and the ship sails from the
            harbour already off track — and the crew won't know it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="destination">Destination</Label>
            <Input
              id="destination"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              placeholder="Where are they trying to go?"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Distance</Label>
              <Select value={distance} onValueChange={(v) => setDistance(v as VoyageDistance)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DISTANCES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Familiarity</Label>
              <Select value={familiarity} onValueChange={(v) => setFamiliarity(v as DestinationFamiliarity)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FAMILIARITIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Starting point</Label>
              <Select value={startKnowledge} onValueChange={(v) => setStartKnowledge(v as StartPointKnowledge)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {START_KNOWLEDGE.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Charts</Label>
              <Select value={chartQuality} onValueChange={(v) => setChartQuality(v as ChartQuality)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CHART_QUALITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded text-sm">
            <p className="font-semibold mb-1">Course Setting DC {dc}</p>
            <pre className="whitespace-pre-wrap text-xs text-gray-600 dark:text-gray-400 font-sans">
              {formatCourseSettingParams(params)}
            </pre>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="course-roll">d20 roll</Label>
              <Input
                id="course-roll"
                type="number"
                value={roll}
                onChange={(e) => setRoll(e.target.value)}
                placeholder="Navigator's d20"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="course-bonus">Skill bonus</Label>
              <Input
                id="course-bonus"
                type="number"
                value={skillBonus}
                onChange={(e) => setSkillBonus(e.target.value)}
              />
            </div>
          </div>

          <Button
            className="w-full"
            onClick={handleBeginVoyage}
            disabled={isStarting || !destination.trim() || roll === ''}
          >
            {isStarting ? 'Setting out...' : 'Begin Voyage'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default VoyageSetup;
